import { useGetCurrentUser } from '@sunstone/api-client/hooks';
import { isFailedPreconditionError } from '@/lib/errors';
import { useAppSelector } from '@/store';
import { useAuthToken } from './useAuthToken';

/**
 * The breadth of tenants this identity can see — role-agnostic by design.
 * 'all' covers any tenant-less account of any role (a tenant-less Admin,
 * or a restricted-only platform user — SUN-408 platform-users addendum);
 * 'partner'/'tenant' come purely from userTenantType once resolved. Being
 * tenant-less/partner/tenant only changes breadth, never whether
 * access_rules narrow deployment/workspace reachability on top
 * (accessrule/evaluate.go is unaffected either way). null only for an
 * unprovisioned/unauthenticated identity, or the transient window before
 * userTenantType resolves for a tenant-scoped account.
 */
export type IdentityScope = 'all' | 'partner' | 'tenant' | null;

/**
 * DB-authoritative version of the identity/authorization flags — sourced
 * from GetCurrentUser (primary_role_id/grants_all/tenant_id), never from
 * the Auth0 JWT's roles/tenant claims. Those claims are set independently
 * of sunstone.users and can go stale (role changed in the DB, migration
 * reset, etc.) — see the backend's own cutover (primary_role_id replacing
 * the JWT role claim for authorization decisions).
 */
export function useAuthorization() {
  const userTenantType = useAppSelector((state) => state.auth.userTenantType);
  const { token, isLoading: tokenLoading } = useAuthToken();
  const { data, isLoading: queryLoading, isError, error } = useGetCurrentUser(token);
  const currentUser = data?.user;

  // Only one unrestricted role exists now (Admin) — SUN-408 retired
  // Operator Admin, promoting every holder to Admin. Admin-ness is purely
  // primary_role_id/isUnrestricted — never tenant_id: a tenant-less Admin
  // (the old Operator Admin's replacement) is still Admin, distinguished
  // from a tenant-scoped Admin only by scope below.
  const isAdmin = !!currentUser && currentUser.primaryRoleIsUnrestricted;

  const scope: IdentityScope = !currentUser
    ? null
    : !currentUser.tenantId
      ? 'all'
      : userTenantType === 'partner'
        ? 'partner'
        : userTenantType === 'child'
          ? 'tenant'
          : null;

  return {
    // Replaces the old 5 (would-be 7, once platform users are added)
    // isSunstoneXxx combinatorial flags with one orthogonal, role-agnostic
    // concept — see IdentityScope's doc comment.
    scope,

    // Manage-access: can administer users/roles, platform-wide (tenant-less
    // Admin) or for their own tenant (tenant-scoped Admin) — deliberately
    // not a permission string. Backend treats it the same way
    // (rbac.RequireManageAccess checks role identity, Admin only) because
    // it's a non-delegable property of that role, never something a custom
    // role can be granted, grants_all or not. scope !== null excludes the
    // 'none'/'pending' loading window before useTenantTypeSync resolves for
    // a tenant-scoped admin.
    canManageAccess: isAdmin && scope !== null,

    // Platform-wide user/role management (Platform Users, System Roles):
    // a tenant-less Admin only. Narrower than canManageAccess (which allows
    // a tenant-scoped Admin managing only their own tenant via
    // scope !== null) — this requires scope === 'all' specifically, since
    // these pages manage users/roles across the whole platform, not one
    // tenant.
    isPlatformAdmin: isAdmin && scope === 'all',

    // Exposed for App.tsx loading gate and AuthGuard
    userTenantType,
    tenantId: currentUser?.tenantId ?? '',
    primaryRoleName: currentUser?.primaryRoleName ?? '',
    primaryRoleGrantsAll: currentUser?.primaryRoleGrantsAll ?? false,
    primaryRoleIsUnrestricted: currentUser?.primaryRoleIsUnrestricted ?? false,

    // True while the DB identity is still being resolved (token pending, or
    // the GetCurrentUser call itself in flight) — AuthGuard should keep
    // showing its loading state, not render children or an error, until
    // this settles.
    isIdentityLoading: tokenLoading || (!!token && queryLoading),
    // True once resolution has actually run and found no sunstone.users row
    // for this identity — AuthGuard's signal to show an explicit
    // access-denied screen instead of letting an empty-data UI render.
    identityError: !!token && isError,
    // True when the account exists but is disabled/deleted (PLAT-124) —
    // distinct from identityError (no account at all) via the backend's
    // FailedPrecondition vs NotFound Connect code. App.tsx's signal to show
    // an "account disabled" screen instead of the generic access-denied one.
    isAccountDisabled: !!token && isFailedPreconditionError(error),

    // Permission-level check (SUN-408 Phase 6 addendum) — a pure permission
    // string lookup. GetCurrentUser always sends the full rbac.AllPermissions
    // list when the caller's role is grants_all, so this needs no grants_all
    // special-case of its own. Action permissions are role-based only — no
    // per-resource narrowing exists any more (access_rules only scope
    // reachability, not permissions) — so this is the single check for every
    // create/edit/delete/etc. action, anywhere.
    hasPermission: (permission: string) =>
      !!currentUser && (currentUser.effectivePermissions ?? []).includes(permission),
  };
}
